import {setHours, setMinutes} from 'date-fns';
import {
  Button,
  ButtonGroup,
  closeDialog,
  DataStatus,
  Form,
  FormControl,
  FormProps,
  FormSubmitHandler,
  openDialog,
  TabsHeader,
} from 'platform/components';
import {Box, Hide, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {isNil, isNotNil, mergeAll} from 'ramda';
import {isNilOrEmpty, isNonEmptyArray} from 'ramda-adjunct';

import {
  documentContextApi,
  useGetDefaultStoreUntilByOrderIdQuery,
  useGetOrderSetDefaultContactPersonQuery,
  useGetTireOrderQuery,
  useGetTireSetLocationQuery,
  useGetTireSetQuery,
  useGetTireSetRequestQuery,
  useGetTireWarehouseSettingsQuery,
  useLazyGetTireSetQuery,
  usePatchTireSetMutation,
  usePatchTireSetRequestMutation,
  usePostTireSetAddWheelsMutation,
  usePostTireSetCancelRequestMutation,
  usePostTireSetMutation,
  usePostTireSetRequestMutation,
  usePostTiresReceiptMutation,
  useUpdateTireSetLocationMutation,
} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {
  generateHashFromObjects,
  getApiDateTimeString,
  isNotNilOrEmpty,
  parseDate,
} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {useGetCurrentBranch} from '../../hooks/useGetCurrentBranch';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {useTenant} from '../../hooks/useTenant';
import {handleApiError} from '../../utils/handleApiError';
import {printPDF} from '../../utils/printPDF';
import {refreshDmsDatagrid} from '../../utils/refreshDmsDatagrid';
import {DmsDataGrid} from '../DmsDataGrid/DmsDataGrid';
import {FullScreenModal} from '../FullScreenModal/FullScreenModal';
import {ArchivedTires} from './components/ArchivedTires';
import {EditTireSetActions} from './components/EditTireSetActions';
import {ExcludedTireSetActions} from './components/ExcludedTireSetActions';
import {StorageWarehouseDialog} from './components/StorageWarehouseDialog';
import {TiresTab} from './components/TiresTab';
import {TIRE_SET_DETAIL_TAB_IDS, TireSetDetailTabId} from './constants/tireSetDetailTabIds';
import {useTireSetControls} from './hooks/useTireSetControls';
import {useTireSetOptions} from './hooks/useTireSetOptions';
import {
  TireSetDetailFormDefinition,
  TireSetDetailFormDefinitionSchema,
} from './types/TireSetDetailFormDefinition';
import {Wheel} from './types/Wheel';
import {getDraftTireSetDefaultValues} from './utils/getDraftTireSetDefaultValues';
import {getDraftTireSetFlags} from './utils/getDraftTireSetFlags';
import {getEditTireSetDefaultValues} from './utils/getEditTireSetDefaultValues';
import {getEditTireSetFlags} from './utils/getEditTireSetFlags';
import {getTireSetHeadline} from './utils/getTireSetHeadline';
import {getTireSetRequestStatus} from './utils/getTireSetRequestStatus';
import {getTireSetStatus} from './utils/getTireSetStatus';

interface TireSetDetailProps {
  tireOrderId: string;
  tireSetId?: string;
  onClose: VoidFunction;
  onEdit?: VoidFunction;
}

export function TireSetDetail(props: TireSetDetailProps) {
  const dispatch = useDispatch();

  const {activeBranchId} = useGetCurrentBranch();

  const [canEditTireSet] = usePermissions({
    permissionKeys: ['editTireSet'],
    scopes: {
      editTireSet: {
        branchId: activeBranchId,
      },
    },
  });

  const {tenantPhoneInfo} = useTenant();

  const [activeTabId, setActiveTabId] = useState<TireSetDetailTabId>(TIRE_SET_DETAIL_TAB_IDS.TIRES);

  const [tireSetId, setTireSetId] = useState<string>(props.tireSetId ?? '');

  const [contactInformation, setContactInformation] =
    useState<TireSetDetailFormDefinition['contactInformation']>(null);

  const formApiRef = useRef<UseFormReturn<TireSetDetailFormDefinition>>(null);

  const isDraft = isNilOrEmpty(tireSetId);

  const tireOrderQuery = useGetTireOrderQuery({
    orderId: props.tireOrderId,
  });

  const tireSetQuery = useGetTireSetQuery(
    {
      orderId: props.tireOrderId,
      setId: tireSetId,
    },
    {skip: isDraft, refetchOnMountOrArgChange: true}
  );

  const defaultStoreUntilQuery = useGetDefaultStoreUntilByOrderIdQuery({
    orderId: props.tireOrderId,
  });

  const defaultContactPersonQuery = useGetOrderSetDefaultContactPersonQuery({
    orderId: props.tireOrderId,
  });

  const customerDataQuery = useGetCustomerV2Query(
    {customerId: tireOrderQuery.data?.customerId ?? ''},
    {skip: isNilOrEmpty(tireOrderQuery.data?.customerId)}
  );

  const tireSetRequestQuery = useGetTireSetRequestQuery(
    {
      orderId: props.tireOrderId,
      setId: tireSetId,
    },
    {skip: isDraft, refetchOnMountOrArgChange: true}
  );

  const tireSetLocationQuery = useGetTireSetLocationQuery(
    {
      orderId: props.tireOrderId,
      setId: tireSetId,
    },
    {skip: isDraft, refetchOnMountOrArgChange: true}
  );

  const tireWarehouseSettingsQuery = useGetTireWarehouseSettingsQuery(undefined, {
    skip: isDraft,
  });

  const [postTireSet, postTireSetStatus] = usePostTireSetMutation();
  const [postTireSetRequest, postTireSetRequestStatus] = usePostTireSetRequestMutation();
  const [getTireSet, getLazyTireSetStatus] = useLazyGetTireSetQuery();
  const [postTireSetReceipt] = usePostTiresReceiptMutation();

  const [patchTireSet] = usePatchTireSetMutation();
  const [postTireSetAddWheels] = usePostTireSetAddWheelsMutation();
  const [updateTireSetLocation] = useUpdateTireSetLocationMutation();
  const [patchTireSetRequest] = usePatchTireSetRequestMutation();
  const [postTireSetCancelRequest] = usePostTireSetCancelRequestMutation();

  const homeBranchId =
    tireOrderQuery.data?.branchId ?? tireSetLocationQuery.data?.storageLocation?.branch?.id;

  const tireSetOptions = useTireSetOptions(homeBranchId);

  const tireSetStatus = getTireSetStatus(tireSetQuery.data?.state);
  const tireSetRequestStatus = getTireSetRequestStatus(tireSetRequestQuery.data?.state);

  const tabs = [
    {
      id: TIRE_SET_DETAIL_TAB_IDS.TIRES,
      title: i18n.t('entity.tireSet.labels.tires'),
    },
    {
      isDisabled: isDraft,
      id: TIRE_SET_DETAIL_TAB_IDS.TIRES_MOVEMENT,
      title: i18n.t('entity.tireSet.labels.tireMovement'),
    },
    {
      id: TIRE_SET_DETAIL_TAB_IDS.TIRES_ARCHIVE,
      title: i18n.t('entity.tireSet.labels.archived'),
    },
  ];

  const headerControls = useTireSetControls({
    vehicleId: tireOrderQuery.data?.vehicleId ?? '',
    customerId: tireOrderQuery.data?.customerId ?? '',
    contactInformation,
  });

  const queryModifier = useCallback(
    (filter: QueryFilterObject) =>
      mergeAll([filter, {orderId: props.tireOrderId, orderSetId: tireSetId}]),
    [props.tireOrderId, tireSetId]
  );

  const renderTabContent = (
    formApi: UseFormReturn<TireSetDetailFormDefinition>,
    control: FormControl<TireSetDetailFormDefinition>
  ) =>
    match(activeTabId)
      .with(TIRE_SET_DETAIL_TAB_IDS.TIRES, () => (
        <TiresTab
          isDraft={isDraft}
          control={control}
          formApi={formApi}
          tireOrderId={props.tireOrderId}
          tireSetId={tireSetId}
          homeBranchId={homeBranchId}
          onContactInformationChange={setContactInformation}
        />
      ))
      .with(TIRE_SET_DETAIL_TAB_IDS.TIRES_MOVEMENT, () => (
        <DmsDataGrid autoHeight gridCode="tire-set-movement" queryModifier={queryModifier} />
      ))
      .with(TIRE_SET_DETAIL_TAB_IDS.TIRES_ARCHIVE, () => <ArchivedTires tireSetId={tireSetId} />)
      .otherwise(() => null);

  const handleSubmit: FormSubmitHandler<TireSetDetailFormDefinition> = async (formValues) => {
    if (isDraft) {
      return await handleSave(formValues, true);
    }

    await handleSave(formValues);

    if (props.tireSetId && tireSetRequestQuery.data && !formValues.tireSetRequestEnabled) {
      await postTireSetCancelRequest({
        orderId: props.tireOrderId,
        setId: tireSetId,
      })
        .unwrap()
        .catch(handleApiError);
    }

    props.onEdit && props.onEdit();
    props.onClose();
  };

  const handleSave = async (
    formValues: TireSetDetailFormDefinition,
    shouldUpdateStorageLocation: boolean = false,
    shouldClose: boolean = true
  ) => {
    if (isDraft) {
      return await postTireSet({
        orderId: props.tireOrderId,
        body: {
          ...formValues,
          wheels: formValues.wheels.map((wheel, index) => ({
            number: String(index + 1),
            wheelDetails: {
              manufacturer: wheel.manufacturer,
              tireWidth: wheel.tireWidth,
              aspectRatio: wheel.aspectRatio,
              rim: wheel.rim,
              tread: wheel.tread,
              rimType: wheel.rimType,
              season: wheel.season,
              li: wheel.li,
              sr: wheel.sr,
              dot: wheel.dot,
              location: wheel.location,
              title: wheel.title,
              hasHubcap: wheel.hasHubcap ?? false,
              hasWheelLocks: wheel.hasWheelLocks ?? false,
              tireCondition: wheel.tireCondition,
              note: wheel.note,
            },
          })),
          contactInformation: {
            ...formValues.contactInformation,
            phoneNumber: {
              ...formValues.contactInformation?.phoneNumber,
              number: parseInt(formValues.contactInformation?.phoneNumber?.number ?? ''),
            },
          },
        },
      })
        .unwrap()
        .then((response) => {
          getTireSet({orderId: props.tireOrderId, setId: response?.setId ?? ''})
            .unwrap()
            .then((tireSet) => {
              const newTireSetId = response?.setId ?? '';
              setTireSetId(newTireSetId);

              formApiRef.current?.setValue(
                'wheels',
                (tireSet?.wheels?.map((wheel) => ({
                  checked: false,
                  id: wheel?.id,
                  aspectRatio: wheel?.wheelDetails?.aspectRatio,
                  dot: wheel?.wheelDetails?.dot,
                  li: wheel?.wheelDetails?.li,
                  location: wheel?.wheelDetails?.location,
                  manufacturer: wheel?.wheelDetails?.manufacturer,
                  note: wheel?.wheelDetails?.note,
                  rim: wheel?.wheelDetails?.rim,
                  rimType: wheel?.wheelDetails?.rimType,
                  season: wheel?.wheelDetails?.season,
                  sr: wheel?.wheelDetails?.sr,
                  tireWidth: wheel?.wheelDetails?.tireWidth,
                  tread: wheel?.wheelDetails?.tread,
                  title: wheel?.wheelDetails?.title,
                  hasHubcap: wheel?.wheelDetails?.hasHubcap ?? false,
                  hasWheelLocks: wheel?.wheelDetails?.hasWheelLocks ?? false,
                  tireCondition: wheel?.wheelDetails?.tireCondition,
                })) as Wheel[]) ?? []
              );

              if (
                formValues.destinationBranchId &&
                formValues.neededAtDate &&
                formValues.neededAtTime
              ) {
                const [hours, minutes] = formValues.neededAtTime.split(':');
                const neededAtDateWithHours = setHours(
                  parseDate(formValues.neededAtDate),
                  parseInt(hours, 10)
                );
                const neededAtDateTime = setMinutes(neededAtDateWithHours, parseInt(minutes, 10));
                const neededAt = getApiDateTimeString(neededAtDateTime);

                const requestDataFromForm = {
                  destinationBranchId: formValues.destinationBranchId,
                  neededAt,
                  note: formValues.requestNote || '',
                };

                return postTireSetRequest({
                  orderId: props.tireOrderId,
                  setId: newTireSetId,
                  body: requestDataFromForm,
                })
                  .unwrap()
                  .then(() => {
                    handleCreatedTireSet(newTireSetId, shouldUpdateStorageLocation);
                  })
                  .catch(handleApiError);
              } else {
                handleCreatedTireSet(newTireSetId, shouldUpdateStorageLocation);
              }
            })
            .catch(handleApiError);
        })
        .then(() => {
          if (shouldClose) {
            props.onClose();
          }
        })
        .catch(handleApiError);
    } else {
      const createdWheels = formValues.wheels.filter((wheel) => isNil(wheel.id));
      const editedWheels = formValues.wheels.filter((wheel) => isNotNil(wheel.id));

      if (isNonEmptyArray(createdWheels)) {
        await postTireSetAddWheels({
          orderId: props.tireOrderId,
          setId: tireSetId,
          body: {
            ...formValues,
            wheels: createdWheels.map((wheel, index) => ({
              number: String(editedWheels.length + index + 1),
              wheelDetails: {
                manufacturer: wheel.manufacturer,
                tireWidth: wheel.tireWidth,
                aspectRatio: wheel.aspectRatio,
                rim: wheel.rim,
                tread: wheel.tread,
                rimType: wheel.rimType,
                season: wheel.season,
                li: wheel.li,
                sr: wheel.sr,
                dot: wheel.dot,
                location: wheel.location,
                title: wheel.title,
                hasHubcap: wheel.hasHubcap ?? false,
                hasWheelLocks: wheel.hasWheelLocks ?? false,
                tireCondition: wheel.tireCondition,
                note: wheel.note,
              },
            })),
          },
        })
          .unwrap()
          .catch(handleApiError);
      }

      const freshTireSet = await getTireSet({
        orderId: props.tireOrderId,
        setId: tireSetId,
      }).unwrap();

      const editedWheelIds = new Set(editedWheels.map((w) => w.id));

      const newWheelsFromServer =
        freshTireSet?.wheels
          ?.filter((wheel) => wheel?.state !== 'ARCHIVED' && !editedWheelIds.has(wheel?.id))
          ?.map((wheel, index) => ({
            id: wheel?.id,
            number: String(editedWheels.length + index + 1),
            wheelDetails: wheel?.wheelDetails,
          })) ?? [];

      const updateTireLocation = tireWarehouseSettingsQuery.data?.isWarehouseAllowed
        ? null
        : updateTireSetLocation({
            orderId: props.tireOrderId,
            setId: tireSetId,
            body: {
              location: formValues.storedLocation ?? '',
              locationDefinition: {contextTarget: 'MANUAL'},
            },
          })
            .unwrap()
            .catch(handleApiError);

      const tireSet = patchTireSet({
        orderId: props.tireOrderId,
        setId: tireSetId,
        body: {
          ...formValues,
          wheels: [
            ...editedWheels.map(({id, ...wheelDetails}, index) => ({
              id,
              number: String(index + 1),
              wheelDetails: {
                ...wheelDetails,
                hasHubcap: wheelDetails?.hasHubcap ?? false,
                hasWheelLocks: wheelDetails?.hasWheelLocks ?? false,
              },
            })),
            ...newWheelsFromServer,
          ],
          contactInformation: {
            ...formValues.contactInformation,
            phoneNumber: {
              ...formValues.contactInformation?.phoneNumber,
              number: parseInt(formValues.contactInformation?.phoneNumber?.number ?? ''),
            },
          },
        },
      })
        .unwrap()
        .catch(handleApiError);

      await Promise.all([tireSet, updateTireLocation]);

      if (
        formValues.tireSetRequestEnabled &&
        formValues.destinationBranchId &&
        formValues.neededAtDate &&
        formValues.neededAtTime
      ) {
        const [hours, minutes] = formValues.neededAtTime.split(':');
        const neededAtDateWithHours = setHours(formValues.neededAtDate, parseInt(hours));
        const neededAtDateTime = setMinutes(neededAtDateWithHours, parseInt(minutes));
        const neededAt = getApiDateTimeString(neededAtDateTime);

        const requestDataFromForm = {
          destinationBranchId: formValues.destinationBranchId,
          neededAt,
          note: formValues.requestNote || '',
        };

        if (tireSetRequestQuery.data) {
          await patchTireSetRequest({
            orderId: props.tireOrderId,
            setId: tireSetId,
            body: requestDataFromForm,
          })
            .unwrap()
            .catch(handleApiError);
        } else {
          await postTireSetRequest({
            orderId: props.tireOrderId,
            setId: tireSetId,
            body: requestDataFromForm,
          })
            .unwrap()
            .catch(handleApiError);
        }
      }
    }
  };

  const refreshDatagrids = () => {
    refreshDmsDatagrid('tire-order-set');
    refreshDmsDatagrid('tire-order-movement');
  };

  const handleCreatedTireSet = (tireSetId: string, shouldUpdateStorageLocation: boolean) => {
    refreshDatagrids();

    if (shouldUpdateStorageLocation) {
      return openStorageLocationDialog(tireSetId);
    }
  };

  const handleStorageLocationSelected = (tireSetId: string) => async () => {
    closeDialog('storageWarehouseDialog');

    const tireSet = await getTireSet({
      orderId: props.tireOrderId,
      setId: tireSetId,
    }).unwrap();

    const wheelIds = tireSet?.wheels?.map((wheel) => ({
      id: wheel?.id,
    }));

    const filteredWheelIds = wheelIds?.filter((wheel) => isNotNilOrEmpty(wheel?.id));

    const response = await postTireSetReceipt({
      orderId: props.tireOrderId,
      setId: tireSetId,
      body: {
        wheels: filteredWheelIds as {id: string}[],
        withoutPrintout: false,
      },
    })
      .unwrap()
      .catch(handleApiError);

    if (response?.printout?.pdfUrl) {
      printPDF(response.printout.pdfUrl);
      dispatch(documentContextApi.util.invalidateTags(['documentsCount']));
      refreshDatagrids();
    }
  };

  const openStorageLocationDialog = (tireSetId: string) => {
    openDialog(
      <StorageWarehouseDialog
        tireSetId={tireSetId}
        tireOrderId={props.tireOrderId}
        onClose={handleStorageLocationSelected(tireSetId)}
        isStockingTires
        data-testid="storageWarehouseDialog"
      />,
      {
        id: 'storageWarehouseDialog',
        title: i18n.t('entity.warehouse.labels.assignStorageLocation'),
        size: 'large',
      }
    );
  };

  const defaultValues: FormProps<TireSetDetailFormDefinition>['defaultValues'] = isDraft
    ? getDraftTireSetDefaultValues(
        tireSetOptions.defaultCategory?.tireCategoryId,
        tireSetOptions.locationOptions,
        defaultStoreUntilQuery.data?.defaultStoreUntil,
        tireSetOptions.defaultManufacturer?.id,
        defaultContactPersonQuery.data,
        tenantPhoneInfo,
        customerDataQuery.data,
        tireSetOptions.defaultTireCondition?.tireConditionId
      )
    : getEditTireSetDefaultValues(
        tenantPhoneInfo,
        tireSetQuery.data,
        tireSetOptions.defaultCategory?.tireCategoryId,
        tireSetLocationQuery.data,
        tireSetRequestQuery.data
      );

  const isLoading =
    tireSetQuery.isLoading ||
    tireSetRequestQuery.isLoading ||
    tireSetLocationQuery.isLoading ||
    tireSetOptions.isLoading ||
    defaultStoreUntilQuery.isLoading ||
    defaultContactPersonQuery.isLoading ||
    customerDataQuery.isLoading;

  const isCreating =
    postTireSetStatus.isLoading ||
    getLazyTireSetStatus.isLoading ||
    postTireSetRequestStatus.isLoading;

  if (isLoading) {
    return (
      <FullScreenModal
        headline={
          isDraft
            ? i18n.t('page.tiresInventory.labels.newTireSet')
            : i18n.t('entity.tireSet.labels.tireSet')
        }
      >
        <DataStatus isLoading minHeight={100} />
      </FullScreenModal>
    );
  }

  const formHash = generateHashFromObjects([
    tireOrderQuery.data,
    tireSetQuery.data?.wheels?.map((wheel) => wheel?.state) ?? [],
  ]);

  return (
    <Form<TireSetDetailFormDefinition>
      experimentalZodSchema={TireSetDetailFormDefinitionSchema}
      defaultValues={defaultValues}
      key={formHash}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        formApiRef.current = formApi;
        return (
          <FullScreenModal
            headline={
              isDraft
                ? i18n.t('page.tiresInventory.labels.newTireSet')
                : getTireSetHeadline(tireSetQuery.data, tireSetRequestQuery.data, {
                    manufacturerOptions: tireSetOptions.manufacturerOptions,
                    tireWidthOptions: tireSetOptions.tireWidthOptions,
                    aspectRatioOptions: tireSetOptions.aspectRatioOptions,
                    rimOptions: tireSetOptions.rimOptions,
                    seasonOptions: tireSetOptions.seasonOptions,
                  })
            }
            flags={
              isDraft
                ? getDraftTireSetFlags(tireSetStatus)
                : getEditTireSetFlags(tireSetStatus, tireSetRequestStatus)
            }
            subtitleActions={headerControls}
            actions={
              isDraft
                ? [
                    {
                      title: i18n.t('general.labels.discard'),
                      variant: 'secondary',
                      onClick: () => props.onClose(),
                    },
                    {
                      isLoading: isCreating,
                      title: i18n.t('general.actions.save'),
                      variant: 'secondary',
                      onClick: () => {
                        if (formApiRef.current) {
                          formApiRef.current.handleSubmit((data) =>
                            handleSave(data, false, false)
                          )();
                        }
                      },
                    },
                    {
                      isLoading: isCreating,
                      title: i18n.t('entity.tireSet.actions.receive'),
                      variant: 'primary',
                      onClick: () => {
                        if (formApiRef.current) {
                          formApiRef.current.handleSubmit((data) => handleSave(data, true))();
                        }
                      },
                    },
                  ]
                : undefined
            }
            customActions={
              isDraft ? undefined : (
                <ButtonGroup>
                  <Button
                    title={i18n.t('general.actions.discard')}
                    variant="secondary"
                    onClick={() => {
                      props.onEdit && props.onEdit();
                      props.onClose();
                    }}
                  />
                  <EditTireSetActions
                    tireOrderId={props.tireOrderId}
                    tireSetId={tireSetId}
                    control={control}
                    formApi={formApi}
                    onClose={props.onClose}
                    onEdit={props.onEdit}
                    saveTires={handleSave}
                    tireSet={tireSetQuery.data}
                    canEditTireSet={canEditTireSet ?? false}
                    homeBranchId={homeBranchId}
                    activeTabId={activeTabId}
                  />
                </ButtonGroup>
              )
            }
          >
            <VStack spacing={0}>
              <Box backgroundColor="general.white" paddingHorizontal={4} width="100%">
                <HStack justify="space-between">
                  <TabsHeader
                    tabs={tabs}
                    activeTabId={activeTabId}
                    onChange={(id) => setActiveTabId(id as TireSetDetailTabId)}
                  />
                  <Hide when={isDraft}>
                    <ExcludedTireSetActions
                      control={control}
                      tireOrderId={props.tireOrderId}
                      tireSetId={tireSetId}
                      homeBranchId={homeBranchId}
                      onClose={props.onClose}
                      onEdit={props.onEdit!}
                    />
                  </Hide>
                </HStack>
              </Box>
              <Box
                backgroundColor="palettes.neutral.20.100"
                padding={4}
                width="100%"
                minHeight="100%"
              >
                {renderTabContent(formApi, control)}
              </Box>
            </VStack>
          </FullScreenModal>
        );
      }}
    </Form>
  );
}
